import { ThemeProvider } from '@repo/ui/components/theme-provider'
import { QueryClientProvider } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { lazy, Suspense, useEffect, useState } from 'react'
import { Toaster } from 'sonner'
import { createQueryClient } from '@/lib/config/query-client'
import { createTrpcClient } from '@/lib/config/trpc-client'
import { trpc } from '@/lib/trpc'

const ReactQueryDevtools = lazy(() =>
  import('@tanstack/react-query-devtools').then((mod) => ({
    default: mod.ReactQueryDevtools,
  }))
)

function useCurrentPathname() {
  const [pathname, setPathname] = useState(() =>
    typeof window === 'undefined' ? '' : window.location.pathname
  )

  useEffect(() => {
    if (typeof window === 'undefined') return

    const updatePathname = () => setPathname(window.location.pathname)
    const originalPushState = window.history.pushState
    const originalReplaceState = window.history.replaceState

    window.history.pushState = function pushState(...args) {
      const result = originalPushState.apply(this, args)
      updatePathname()
      return result
    }

    window.history.replaceState = function replaceState(...args) {
      const result = originalReplaceState.apply(this, args)
      updatePathname()
      return result
    }

    window.addEventListener('popstate', updatePathname)
    window.addEventListener('hashchange', updatePathname)
    updatePathname()

    return () => {
      window.history.pushState = originalPushState
      window.history.replaceState = originalReplaceState
      window.removeEventListener('popstate', updatePathname)
      window.removeEventListener('hashchange', updatePathname)
    }
  }, [])

  return pathname
}

export function Providers({ children }: { children: ReactNode }) {
  const [queryClient] = useState(() => createQueryClient())
  const [trpcClient] = useState(() => createTrpcClient())
  const pathname = useCurrentPathname()
  const shouldShowDevtools =
    import.meta.env.DEV && !pathname.startsWith('/auth') && !pathname.startsWith('/setup')

  return (
    <QueryClientProvider client={queryClient}>
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <ThemeProvider>
          {children}

          <Toaster />
          {shouldShowDevtools && (
            <Suspense fallback={null}>
              <ReactQueryDevtools initialIsOpen={false} />
            </Suspense>
          )}
        </ThemeProvider>
      </trpc.Provider>
    </QueryClientProvider>
  )
}
